import { Flex, Radio, RadioGroup, Stack, Text } from '@chakra-ui/react';
import { CHART_EDITED, Mixpanel } from 'libs/third-party/Mixpanel';
import {
  ChartSettingsDivider,
  ChartSliderInput,
  SettingsAccordion,
  Switcher,
} from 'modules/Editor/components/SideMenuSettings/ChartSettings';
import { useTranslation } from 'react-i18next';
import { WidgetType } from 'types/widget.types';
import { getChartInfo } from 'widgets/ChartWidgets/ChartWidget.helpers';
import { ChartWidgetData } from 'widgets/ChartWidgets/ChartWidget.types';
import {
  DEFAULT_CONNECTOR_WIDTH,
  MAX_LINE_CONNECTOR_WIDTH,
  MIN_LINE_CONNECTOR_WIDTH,
} from 'widgets/ChartWidgets/PyramidChartWidget/PyramidChartWidget.config';
import { useEditor, useWidget } from 'widgets/sdk';

import { DataLabelFont, DataLabelText } from './ChartSettingsLabel';

interface ChartSettingsLabelPyramidProps {
  widgetType: WidgetType;
}

export const ChartSettingsLabelPyramid = ({ widgetType }: ChartSettingsLabelPyramidProps) => {
  const {
    dataLabels: { categoryEnabled, valueEnabled, enabled, inside: isInside },
    updateWidget,
  } = useWidget<ChartWidgetData>();
  const { t } = useTranslation('chart_widget', { useSuspense: false });

  // if the categoryEnabled or valueEnabled is not defined, use the enabled value (can be the case for old widgets)
  const isValueEnabled = typeof valueEnabled === 'undefined' ? enabled : valueEnabled;
  const isCategoryEnabled = typeof categoryEnabled === 'undefined' ? enabled : categoryEnabled;

  const onToggleCategoryLabel = () => {
    updateWidget({ dataLabels: { categoryEnabled: !isCategoryEnabled } });
    Mixpanel.track(CHART_EDITED, {
      styling_property: CHART_EDITED.styling_property.Labels,
      styling_value: !categoryEnabled
        ? CHART_EDITED.styling_value.CategoryLabelsOn
        : CHART_EDITED.styling_value.CategoryLabelsOff,
      chart_type: getChartInfo(widgetType).widgetTypeName,
    });
  };

  const onToggleValueLabel = () => {
    updateWidget({ dataLabels: { valueEnabled: !isValueEnabled } });
    Mixpanel.track(CHART_EDITED, {
      styling_property: CHART_EDITED.styling_property.Labels,
      styling_value: !valueEnabled
        ? CHART_EDITED.styling_value.ValueLabelsOn
        : CHART_EDITED.styling_value.ValueLabelsOff,
      chart_type: getChartInfo(widgetType).widgetTypeName,
    });
  };

  return (
    <SettingsAccordion title={t('labels.label')} isDefaultOpen={true}>
      <Switcher label={t('labels.showCategories')} onToggle={onToggleCategoryLabel} isChecked={isCategoryEnabled} />
      <Switcher
        label={t('labels.showValue')}
        onToggle={onToggleValueLabel}
        isChecked={isValueEnabled}
        style={{ marginTop: 0 }}
      />
      <DataLabelPositionControls />
      {!isInside && <DataLabelConnectorWidth />}
      <ChartSettingsDivider />
      <DataLabelFont />
      <DataLabelText showFontColor />
    </SettingsAccordion>
  );
};

/**
 * Data label position controls
 */
const DataLabelPositionControls = () => {
  const { boundingBox } = useEditor();
  const { dataLabels, updateWidget } = useWidget<ChartWidgetData>();
  const { t } = useTranslation('chart_widget', { useSuspense: false, keyPrefix: 'funnel' });

  const onSetPosition = (position: string) => {
    updateWidget({ dataLabels: { inside: position === 'inside' } });

    // Update widget bounding box after data label positions are changed from inside / outside
    return setTimeout(() => boundingBox.updateRect(), 0);
  };

  return (
    <>
      <RadioGroup my={2} onChange={onSetPosition} defaultValue={!!dataLabels?.inside ? 'inside' : 'outside'}>
        <Flex direction='row' justifyContent='space-between'>
          <Text fontSize='sm'>{t('positionLabel')}</Text>
          <Stack spacing={5} direction='row'>
            <Radio value='inside'>
              <Text fontSize='sm'>{t('insideLabel')}</Text>
            </Radio>
            <Radio value='outside'>
              <Text fontSize='sm'>{t('outsideLabel')}</Text>
            </Radio>
          </Stack>
        </Flex>
      </RadioGroup>
    </>
  );
};

const DataLabelConnectorWidth = () => {
  const { dataLabels, updateWidget } = useWidget<ChartWidgetData>();
  const { t } = useTranslation('chart_widget', { useSuspense: false, keyPrefix: 'funnel' });

  const onConnectorWidthChange = (value: number) => {
    updateWidget({ dataLabels: { connectorWidth: value } });
  };

  return (
    <ChartSliderInput
      title={t('connectorWidthLabel')}
      value={dataLabels?.connectorWidth ?? DEFAULT_CONNECTOR_WIDTH}
      onChange={onConnectorWidthChange}
      minValue={MIN_LINE_CONNECTOR_WIDTH}
      maxValue={MAX_LINE_CONNECTOR_WIDTH}
      suffix={'px'}
    />
  );
};
